import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { COVER_BG_IMAGE } from '../constants/cover';
import { teamsAPI, matchesAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaArrowRight, FaFutbol, FaMapMarkerAlt, FaBuilding, FaTrophy, FaChartLine, FaCalendar, FaHistory } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useLanguage } from '../context/LanguageContext';

const TeamDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const [team, setTeam] = useState(null);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [players, setPlayers] = useState([]);
  const [penalties, setPenalties] = useState([]);

  useEffect(() => {
    loadTeamData();
  }, [id]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      const apiBase = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      const [teamsRes, matchesRes, standingsRes, playersRes, penaltiesRes] = await Promise.all([
        teamsAPI.getAll(),
        matchesAPI.getAll(),
        matchesAPI.getStandings(),
        (typeof (teamsAPI.getAllPlayers) === 'function' ? teamsAPI.getAllPlayers() : fetch(`${apiBase}/api/content/players`).then(r => r.json()).catch(() => ({}))),
        contentAPI.getPenalties().catch(() => ({ data: [] }))
      ]);

      const teamsList = Array.isArray(teamsRes?.data) ? teamsRes.data : (Array.isArray(teamsRes) ? teamsRes : []);
      const teamId = parseInt(id, 10);
      const currentTeam = teamsList.find(t => Number(t.id) === teamId) || teamsList.find(t => String(t.id) === id);
      setTeam(currentTeam || null);

      const pensList = Array.isArray(penaltiesRes?.data) ? penaltiesRes.data : (Array.isArray(penaltiesRes) ? penaltiesRes : []);
      setPenalties(pensList.filter(p => Number(p.team_id) === teamId));

      const matchesList = Array.isArray(matchesRes?.data) ? matchesRes.data : (Array.isArray(matchesRes) ? matchesRes : []);
      const teamMatches = matchesList.filter(
        m => Number(m.home_team_id) === teamId || Number(m.away_team_id) === teamId
      );
      setMatches(teamMatches);

      const standList = Array.isArray(standingsRes?.data) ? standingsRes.data : (Array.isArray(standingsRes) ? standingsRes : []);
      setStandings(standList);

      const playersData = playersRes?.data ?? playersRes;
      const allPlayers = Array.isArray(playersData) ? playersData : [];
      const teamPlayers = allPlayers.filter(p => Number(p.team_id) === teamId);
      setPlayers(teamPlayers);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!team) {
    return (
      <div style={styles.container}>
        <div style={styles.notFound}>
          <h2>{language === 'ar' ? 'الفريق غير موجود' : 'Équipe non trouvée'}</h2>
          <Link to="/teams" style={styles.backLink}>
            {language === 'ar' ? 'العودة إلى الفرق' : 'Retour aux équipes'}
          </Link>
        </div>
      </div>
    );
  }

  const teamStanding = standings.find(s => s.id === team.id);
  const finishedMatches = matches.filter(m => m.status === 'finished').sort((a, b) =>
    new Date(b.match_date) - new Date(a.match_date)
  );
  const upcomingMatches = matches.filter(m => m.status === 'upcoming').sort((a, b) =>
    new Date(a.match_date) - new Date(b.match_date)
  );

  // Calculer les statistiques
  const wins = finishedMatches.filter(m =>
    (m.home_team_id === team.id && m.home_score > m.away_score) ||
    (m.away_team_id === team.id && m.away_score > m.home_score)
  ).length;

  const draws = finishedMatches.filter(m => m.home_score === m.away_score).length;
  const losses = finishedMatches.length - wins - draws;

  const goalsScored = finishedMatches.reduce((sum, m) => {
    return sum + (m.home_team_id === team.id ? m.home_score : m.away_score);
  }, 0);

  const goalsConceded = finishedMatches.reduce((sum, m) => {
    return sum + (m.home_team_id === team.id ? m.away_score : m.home_score);
  }, 0);

  const text = {
    fr: {
      overview: 'Aperçu',
      results: 'Résultats',
      upcoming: 'Matchs à venir',
      stats: 'Statistiques',
      position: 'Position',
      points: 'Points',
      played: 'J',
      wins: 'V',
      draws: 'N',
      losses: 'D',
      goalsFor: 'BP',
      goalsAgainst: 'BC',
      goalDiff: 'Diff',
      lastMatches: 'Derniers matchs',
      nextMatches: 'Prochains matchs',
      noMatches: 'Aucun match',
      vs: 'vs',
      home: 'Domicile',
      away: 'Extérieur',
      players: 'Joueurs',
      history: 'Histoire',
      board: 'Direction',
      noPlayers: 'Aucun joueur enregistré',
      noHistory: 'Histoire non renseignée',
      noBoard: 'Bureau directeur non renseigné'
    },
    ar: {
      overview: 'نظرة عامة',
      results: 'النتائج',
      upcoming: 'المباريات القادمة',
      stats: 'الإحصائيات',
      position: 'المركز',
      points: 'النقاط',
      played: 'لعب',
      wins: 'فوز',
      draws: 'تعادل',
      losses: 'خسارة',
      goalsFor: 'له',
      goalsAgainst: 'عليه',
      goalDiff: 'الفارق',
      lastMatches: 'آخر المباريات',
      nextMatches: 'المباريات القادمة',
      noMatches: 'لا توجد مباريات',
      vs: 'ضد',
      home: 'على أرضه',
      away: 'خارج الديار',
      players: 'اللاعبين',
      history: 'تاريخ النادي',
      board: 'الهيئة المديرة',
      noPlayers: 'لا يوجد لاعبون مسجلون',
      noHistory: 'التاريخ غير متوفر حالياً',
      noBoard: 'لا توجد معلومات عن الهيئة المديرة'
    }
  };

  const t = text[language] || text.fr;

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <Link to="/teams" style={styles.backButton}>
          <FaArrowRight /> {language === 'ar' ? 'العودة' : 'Retour'}
        </Link>

        <div style={styles.teamHeader}>
          {getLogoUrl(team.logo_url) ? (
            <img
              src={getLogoUrl(team.logo_url)}
              alt={team.name}
              style={styles.teamLogo}
              onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div style={{...styles.logoPlaceholder, display: getLogoUrl(team.logo_url) ? 'none' : 'flex'}}>
            <FaFutbol size={80} color="white" />
          </div>

          <div style={styles.teamInfo}>
            <h1 style={styles.teamName}>{team.name}</h1>
            <div style={styles.teamMeta}>
              <div style={styles.metaItem}>
                <FaMapMarkerAlt /> {team.city || language === 'ar' ? 'غير محدد' : 'Non spécifié'}
              </div>
              {team.stadium && (
                <div style={styles.metaItem}>
                  <FaBuilding /> {team.stadium}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {teamStanding && (
          <div style={styles.quickStats}>
            <div style={styles.quickStat}>
              <FaTrophy style={styles.quickStatIcon} />
              <div>
                <div style={styles.quickStatValue}>{teamStanding.position || standings.findIndex(s => s.id === team.id) + 1}</div>
                <div style={styles.quickStatLabel}>{t.position}</div>
              </div>
            </div>
            <div style={styles.quickStat}>
              <FaChartLine style={styles.quickStatIcon} />
              <div>
                <div style={styles.quickStatValue}>{teamStanding.points}</div>
                <div style={styles.quickStatLabel}>{t.points}</div>
              </div>
            </div>
            <div style={styles.quickStat}>
              <FaFutbol style={styles.quickStatIcon} />
              <div>
                <div style={styles.quickStatValue}>{teamStanding.played}</div>
                <div style={styles.quickStatLabel}>{t.played}</div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Tabs */}
      <section style={styles.tabsSection}>
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'overview' && styles.activeTab) }}
            onClick={() => setActiveTab('overview')}
          >
            <FaChartLine /> {t.overview}
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'results' && styles.activeTab) }}
            onClick={() => setActiveTab('results')}
          >
            <FaHistory /> {t.results}
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'upcoming' && styles.activeTab) }}
            onClick={() => setActiveTab('upcoming')}
          >
            <FaCalendar /> {t.upcoming}
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'players' && styles.activeTab) }}
            onClick={() => setActiveTab('players')}
          >
            <FaFutbol /> {t.players}
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'board' && styles.activeTab) }}
            onClick={() => setActiveTab('board')}
          >
            <FaBuilding /> {t.board}
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'history' && styles.activeTab) }}
            onClick={() => setActiveTab('history')}
          >
            <FaHistory /> {t.history}
          </button>
          {penalties.length > 0 && (
            <button
              style={{ ...styles.tab, ...(activeTab === 'sanctions' && styles.activeTab), color: '#dc3545' }}
              onClick={() => setActiveTab('sanctions')}
            >
              <FaExclamationTriangle /> {language === 'ar' ? 'العقوبات' : 'Sanctions'}
            </button>
          )}
        </div>
      </section>

      {/* Content */}
      <section style={styles.content}>
        {activeTab === 'overview' && (
          <div style={styles.overviewGrid}>
            {/* Statistics Card */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>
                <FaChartLine /> {t.stats}
              </h3>
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statValue}>{finishedMatches.length}</div>
                  <div style={styles.statLabel}>{t.played}</div>
                </div>
                <div style={{ ...styles.statItem, borderLeft: '2px solid #e0e0e0' }}>
                  <div style={{ ...styles.statValue, color: '#4CAF50' }}>{wins}</div>
                  <div style={styles.statLabel}>{t.wins}</div>
                </div>
                <div style={{ ...styles.statItem, borderLeft: '2px solid #e0e0e0' }}>
                  <div style={{ ...styles.statValue, color: '#FFC107' }}>{draws}</div>
                  <div style={styles.statLabel}>{t.draws}</div>
                </div>
                <div style={{ ...styles.statItem, borderLeft: '2px solid #e0e0e0' }}>
                  <div style={{ ...styles.statValue, color: '#f44336' }}>{losses}</div>
                  <div style={styles.statLabel}>{t.losses}</div>
                </div>
              </div>

              <div style={styles.goalsStats}>
                <div style={styles.goalStat}>
                  <span style={styles.goalLabel}>{t.goalsFor}:</span>
                  <span style={{ ...styles.goalValue, color: '#4CAF50' }}>{goalsScored}</span>
                </div>
                <div style={styles.goalStat}>
                  <span style={styles.goalLabel}>{t.goalsAgainst}:</span>
                  <span style={{ ...styles.goalValue, color: '#f44336' }}>{goalsConceded}</span>
                </div>
                <div style={styles.goalStat}>
                  <span style={styles.goalLabel}>{t.goalDiff}:</span>
                  <span style={{
                    ...styles.goalValue,
                    color: goalsScored - goalsConceded > 0 ? '#4CAF50' : '#f44336'
                  }}>
                    {goalsScored - goalsConceded > 0 ? '+' : ''}{goalsScored - goalsConceded}
                  </span>
                </div>
              </div>
            </div>

            {/* Last 5 Matches */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>
                <FaHistory /> {t.lastMatches}
              </h3>
              {finishedMatches.slice(0, 5).length > 0 ? (
                <div style={styles.matchesList}>
                  {finishedMatches.slice(0, 5).map(match => (
                    <MatchItem key={match.id} match={match} team={team} language={language} />
                  ))}
                </div>
              ) : (
                <p style={styles.noData}>{t.noMatches}</p>
              )}
            </div>

            {/* Next Matches */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>
                <FaCalendar /> {t.nextMatches}
              </h3>
              {upcomingMatches.slice(0, 3).length > 0 ? (
                <div style={styles.matchesList}>
                  {upcomingMatches.slice(0, 3).map(match => (
                    <MatchItem key={match.id} match={match} team={team} language={language} upcoming />
                  ))}
                </div>
              ) : (
                <p style={styles.noData}>{t.noMatches}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'results' && (
          <div style={styles.matchesContainer}>
            <h3 style={styles.sectionTitle}>{t.lastMatches}</h3>
            {finishedMatches.length > 0 ? (
              <div style={styles.matchesGrid}>
                {finishedMatches.map(match => (
                  <MatchCard key={match.id} match={match} team={team} language={language} />
                ))}
              </div>
            ) : (
              <p style={styles.noData}>{t.noMatches}</p>
            )}
          </div>
        )}

        {activeTab === 'upcoming' && (
          <div style={styles.matchesContainer}>
            <h3 style={styles.sectionTitle}>{t.nextMatches}</h3>
            {upcomingMatches.length > 0 ? (
              <div style={styles.matchesGrid}>
                {upcomingMatches.map(match => (
                  <MatchCard key={match.id} match={match} team={team} language={language} upcoming />
                ))}
              </div>
            ) : (
              <p style={styles.noData}>{t.noMatches}</p>
            )}
          </div>
        )}

        {activeTab === 'players' && (
          <div style={styles.playersGrid}>
            {players.length > 0 ? (
              players.map(player => (
                <div key={player.id} style={styles.playerCard}>
                  <div style={styles.playerNumber}>{player.number}</div>
                  <div style={styles.playerInfo}>
                    <div style={styles.playerName}>{language === 'ar' ? player.name_ar || player.name : player.name}</div>
                    <div style={styles.playerPosition}>{player.position}</div>
                  </div>
                  <div style={styles.playerStats}>
                    <div style={styles.playerStat}>
                      <span>⚽ {player.goals}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.noDataCard}>{t.noPlayers}</div>
            )}
          </div>
        )}

        {activeTab === 'board' && (
          <div style={styles.boardContainer}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}><FaBuilding /> {t.board}</h3>
              <div style={styles.boardContent}>
                {language === 'ar'
                  ? (team.board_members_ar || t.noBoard)
                  : (team.board_members || t.noBoard)
                }
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div style={styles.historyContainer}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}><FaHistory /> {t.history}</h3>
              <div style={styles.historyContent}>
                {language === 'ar'
                  ? (team.history_ar || t.noHistory)
                  : (team.history || t.noHistory)
                }
              </div>
            </div>
          </div>
        )}
        {activeTab === 'sanctions' && (
          <div style={styles.historyContainer}>
            <div style={styles.card}>
              <h3 style={{ ...styles.cardTitle, color: '#dc3545' }}>
                <FaExclamationTriangle /> {language === 'ar' ? 'سجل العقوبات' : 'Historique des Sanctions'}
              </h3>
              <div style={styles.sanctionsList}>
                {penalties.map(p => (
                  <div key={p.id} style={styles.sanctionItemDetail}>
                    <div style={styles.sanctionHeader}>
                      <span style={styles.sanctionPoints}>-{p.points} Pts</span>
                      <span style={styles.sanctionDate}>{new Date(p.created_at).toLocaleDateString()}</span>
                    </div>
                    <div style={styles.sanctionReason}>
                      {language === 'ar' ? p.reason_ar : p.reason}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

// Composant pour afficher un match dans la liste
const MatchItem = ({ match, team, language, upcoming = false }) => {
  const isHome = match.home_team_id === team.id;
  const opponent = isHome ? match.away_team_name : match.home_team_name;
  const teamScore = isHome ? match.home_score : match.away_score;
  const opponentScore = isHome ? match.away_score : match.home_score;

  let result = '';
  if (!upcoming) {
    if (teamScore > opponentScore) result = 'win';
    else if (teamScore < opponentScore) result = 'loss';
    else result = 'draw';
  }

  return (
    <div style={styles.matchItem}>
      <div style={styles.matchItemDate}>
        {new Date(match.match_date).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
          day: 'numeric',
          month: 'short'
        })}
      </div>
      <div style={styles.matchItemInfo}>
        <div style={styles.matchItemOpponent}>
          {language === 'ar' ? 'ضد' : 'vs'} {opponent}
        </div>
        <div style={styles.matchItemLocation}>
          {isHome ? (language === 'ar' ? '🏠 على أرضه' : '🏠 Domicile') : (language === 'ar' ? '✈️ خارج الديار' : '✈️ Extérieur')}
        </div>
      </div>
      {!upcoming && (
        <div style={{
          ...styles.matchItemResult,
          background: result === 'win' ? '#4CAF50' : result === 'loss' ? '#f44336' : '#FFC107'
        }}>
          {teamScore} - {opponentScore}
        </div>
      )}
      {upcoming && (
        <div style={{ ...styles.matchItemResult, background: '#2196F3' }}>
          {new Date(match.match_date).toLocaleTimeString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};

// Composant pour afficher un match en carte
const MatchCard = ({ match, team, language, upcoming = false }) => {
  const isHome = match.home_team_id === team.id;

  return (
    <div style={styles.matchCard}>
      <div style={styles.matchCardDate}>
        {new Date(match.match_date).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })}
      </div>

      <div style={styles.matchCardTeams}>
        <div style={styles.matchCardTeam}>
          {match.home_team_logo && (
            <img src={typeof window !== 'undefined' ? window.location.origin + match.home_team_logo : match.home_team_logo} alt="" style={styles.matchCardLogo} />
          )}
          <span style={{ ...styles.matchCardTeamName, fontWeight: isHome ? 'bold' : 'normal' }}>
            {match.home_team_name}
          </span>
        </div>

        <div style={styles.matchCardScore}>
          {!upcoming ? (
            <>
              <span style={styles.scoreNumber}>{match.home_score}</span>
              <span style={styles.scoreSeparator}>-</span>
              <span style={styles.scoreNumber}>{match.away_score}</span>
            </>
          ) : (
            <span style={styles.vsText}>VS</span>
          )}
        </div>

        <div style={styles.matchCardTeam}>
          {match.away_team_logo && (
            <img src={typeof window !== 'undefined' ? window.location.origin + match.away_team_logo : match.away_team_logo} alt="" style={styles.matchCardLogo} />
          )}
          <span style={{ ...styles.matchCardTeamName, fontWeight: !isHome ? 'bold' : 'normal' }}>
            {match.away_team_name}
          </span>
        </div>
      </div>

      {upcoming && (
        <div style={styles.matchCardTime}>
          ⏰ {new Date(match.match_date).toLocaleTimeString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '40px 20px 60px',
    color: 'white',
    position: 'relative'
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'white',
    textDecoration: 'none',
    fontSize: '16px',
    marginBottom: '30px',
    padding: '10px 20px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '25px',
    transition: 'all 0.3s'
  },
  teamHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    maxWidth: '1200px',
    margin: '0 auto 40px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  teamLogo: {
    width: '150px',
    height: '150px',
    objectFit: 'contain',
    background: 'white',
    borderRadius: '20px',
    padding: '20px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
  },
  logoPlaceholder: {
    width: '150px',
    height: '150px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  teamInfo: {
    flex: 1,
    minWidth: '300px'
  },
  teamName: {
    fontSize: '42px',
    fontWeight: 'bold',
    marginBottom: '15px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  teamMeta: {
    display: 'flex',
    gap: '25px',
    fontSize: '18px',
    flexWrap: 'wrap'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: 0.9
  },
  quickStats: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    maxWidth: '1200px',
    margin: '0 auto',
    flexWrap: 'wrap'
  },
  quickStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    background: 'rgba(255,255,255,0.2)',
    padding: '20px 30px',
    borderRadius: '15px',
    backdropFilter: 'blur(10px)'
  },
  quickStatIcon: {
    fontSize: '32px'
  },
  quickStatValue: {
    fontSize: '28px',
    fontWeight: 'bold'
  },
  quickStatLabel: {
    fontSize: '14px',
    opacity: 0.9
  },
  tabsSection: {
    background: 'white',
    borderBottom: '2px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  tabs: {
    display: 'flex',
    maxWidth: '1200px',
    margin: '0 auto',
    gap: '10px',
    padding: '0 20px'
  },
  tab: {
    flex: 1,
    padding: '15px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s'
  },
  activeTab: {
    color: '#c41e3a',
    borderBottomColor: '#c41e3a'
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px'
  },
  card: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#333',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    marginBottom: '20px'
  },
  statItem: {
    textAlign: 'center',
    padding: '15px'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '5px'
  },
  statLabel: {
    fontSize: '14px',
    color: '#666'
  },
  goalsStats: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '20px',
    background: '#f9f9f9',
    borderRadius: '10px'
  },
  goalStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px'
  },
  goalLabel: {
    fontSize: '14px',
    color: '#666'
  },
  goalValue: {
    fontSize: '24px',
    fontWeight: 'bold'
  },
  matchesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  matchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: '#f9f9f9',
    borderRadius: '10px',
    transition: 'all 0.3s'
  },
  matchItemDate: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#666',
    minWidth: '60px'
  },
  matchItemInfo: {
    flex: 1
  },
  matchItemOpponent: {
    fontSize: '15px',
    fontWeight: '600',
    marginBottom: '4px'
  },
  matchItemLocation: {
    fontSize: '13px',
    color: '#666'
  },
  matchItemResult: {
    padding: '8px 16px',
    borderRadius: '8px',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '16px',
    minWidth: '60px',
    textAlign: 'center'
  },
  matchesContainer: {
    width: '100%'
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '25px',
    color: '#333'
  },
  matchesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '25px'
  },
  matchCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    transition: 'all 0.3s'
  },
  matchCardDate: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px',
    textAlign: 'center'
  },
  matchCardTeams: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px'
  },
  matchCardTeam: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  },
  matchCardLogo: {
    width: '60px',
    height: '60px',
    objectFit: 'contain'
  },
  matchCardTeamName: {
    fontSize: '14px',
    textAlign: 'center'
  },
  matchCardScore: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  scoreNumber: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#c41e3a'
  },
  scoreSeparator: {
    fontSize: '24px',
    color: '#999'
  },
  vsText: {
    fontSize: '20px',
    color: '#999',
    fontWeight: 'bold'
  },
  matchCardTime: {
    textAlign: 'center',
    marginTop: '15px',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#2196F3'
  },
  noData: {
    textAlign: 'center',
    color: '#999',
    padding: '40px',
    fontSize: '16px'
  },
  notFound: {
    textAlign: 'center',
    padding: '100px 20px',
    color: '#666'
  },
  backLink: {
    display: 'inline-block',
    marginTop: '20px',
    padding: '12px 30px',
    background: '#c41e3a',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '25px',
    fontSize: '16px'
  },
  playersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
    marginTop: '20px'
  },
  playerCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    border: '1px solid #eee'
  },
  playerNumber: {
    width: '45px',
    height: '45px',
    background: '#c41e3a',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold'
  },
  playerInfo: {
    flex: 1
  },
  playerName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  playerPosition: {
    fontSize: '14px',
    color: '#666',
    marginTop: '4px'
  },
  playerStats: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#c41e3a'
  },
  noDataCard: {
    background: 'white',
    padding: '40px',
    borderRadius: '15px',
    textAlign: 'center',
    color: '#999',
    width: '100%',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
  },
  boardContent: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#444',
    whiteSpace: 'pre-line'
  },
  historyContent: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#444',
    whiteSpace: 'pre-line'
  },
  sanctionsList: { display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' },
  sanctionItemDetail: { padding: '15px', background: '#fff5f5', borderLeft: '4px solid #dc3545', borderRadius: '8px' },
  sanctionHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' },
  sanctionPoints: { fontWeight: 'bold', color: '#dc3545', background: 'white', padding: '2px 10px', borderRadius: '15px', fontSize: '14px' },
  sanctionDate: { fontSize: '12px', color: '#888' },
  sanctionReason: { fontWeight: '500', color: '#444' }
};

export default TeamDetailPage;
